import { jsx as _jsx } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sharepointClient } from '../lib/connect/clients';
import { useAuth } from './authContext';
const SharePointAuthContext = /*#__PURE__*/ createContext(undefined);
// Only a request WE aborted looks like this. DOMException does not extend Error
// and undici's copy is not jsdom's, so match on the name rather than the class —
// a real connection failure (TypeError: Failed to fetch, CORS, DNS) keeps its
// own name and must still surface.
// An unmount mid-request is not a failure, and it must stay silent whichever
// layer reports it. A bare fetch rejects with a DOMException named AbortError;
// the Connect transport catches that same abort and re-throws it as a
// ConnectError whose name is "ConnectError" and whose code is Canceled. Only
// checking the name — which is all this did while the call was plain fetch —
// silently stops recognising an abort the moment the call goes through Connect,
// and every navigation starts logging an error it should not.
function isAbortError(err) {
    if (typeof err !== 'object' || err === null) {
        return false;
    }
    if (err.name === 'AbortError') {
        return true;
    }
    return err instanceof ConnectError && err.code === Code.Canceled;
}
export function SharePointAuthProvider({ children }) {
    const [isSharePointConnected, setIsSharePointConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Whose SharePoint session this even is only has an answer once the user is
    // known. The provider is mounted app-wide, so without this the check fires on
    // the login page and is answered with a 401 — which express returned too, but
    // silently, because a REST 401 was just !response.ok. Connect throws, so the
    // same pre-auth call now surfaces as a logged error and a failed network gate.
    // Not asking until there is a principal is both quieter and more honest than
    // teaching the handler to swallow it.
    const { isAuthenticated } = useAuth();
    // Check if user has an active SharePoint session once authenticated
    useEffect(()=>{
        if (!isAuthenticated) {
            setIsSharePointConnected(false);
            setIsLoading(false);
            return;
        }
        // This provider is mounted app-wide, so a navigation can unmount it with the
        // session check still in flight. Abort it on cleanup so the rejection is an
        // unambiguous AbortError instead of a bare "Failed to fetch".
        const controller = new AbortController();
        const checkSharePointSession = async ()=>{
            try {
                setIsLoading(true);
                setError(null);
                // user_id and tenant_id are deliberately omitted: enies-lobby takes
                // them from the gateway principal for browser traffic and ignores the
                // request body, so sending them would be decorative at best and
                // misleading at worst.
                const response = await sharepointClient.getSession({}, {
                    signal: controller.signal
                });
                if (!controller.signal.aborted) {
                    setIsSharePointConnected(response.connected);
                }
            } catch (err) {
                // We aborted it ourselves on unmount: nothing failed and nobody is left
                // to tell. Every other failure still logs and still reports disconnected.
                if (isAbortError(err)) {
                    return;
                }
                // An expired or rejected session is an answer, not a fault: it means
                // not connected. Logging it would trip the console gate on something
                // the user resolves by signing in again.
                if (err instanceof ConnectError && err.code === Code.Unauthenticated) {
                    if (!controller.signal.aborted) {
                        setIsSharePointConnected(false);
                    }
                    return;
                }
                console.error('Failed to check SharePoint session:', err);
                if (!controller.signal.aborted) {
                    setIsSharePointConnected(false);
                }
            } finally{
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };
        checkSharePointSession();
        return ()=>controller.abort();
    }, [
        isAuthenticated
    ]);
    const initiateOAuthFlow = useCallback(async ()=>{
        try {
            setIsLoading(true);
            setError(null);
            // Call backend to get Microsoft OAuth authorization URL. Pass the current
            // path as returnTo so the callback brings the user back to where they
            // started the flow (e.g. /sharepoint-documents) instead of /settings.
            const redirectUri = '/api/sharepoint/oauth/callback';
            const returnTo = window.location.pathname + window.location.search;
            const params = new URLSearchParams({
                redirectUri,
                returnTo
            });
            const response = await fetch(`/api/sharepoint/oauth/authorize?${params.toString()}`, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                const errorData = await response.json().catch(()=>({}));
                throw new Error(errorData.message || `Failed to get authorization URL: ${response.statusText}`);
            }
            const data = await response.json();
            const authorizationUrl = data.authorizationUrl;
            if (!authorizationUrl) {
                throw new Error('No authorization URL returned from server');
            }
            // Redirect to Microsoft OAuth login page
            window.location.href = authorizationUrl;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to initiate SharePoint OAuth';
            setError(message);
            setIsLoading(false);
            throw err;
        }
    }, []);
    const connectSharePoint = useCallback(async ()=>{
        try {
            await initiateOAuthFlow();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to initiate SharePoint connection';
            toast.error(message);
        }
    }, [
        initiateOAuthFlow
    ]);
    const disconnectSharePoint = useCallback(async ()=>{
        try {
            setIsLoading(true);
            setError(null);
            const response = await sharepointClient.revokeSession({});
            if (!response.success) {
                throw new Error(response.message || 'Failed to disconnect from SharePoint');
            }
            setIsSharePointConnected(false);
            toast.success('SharePoint disconnected');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to disconnect from SharePoint';
            setError(message);
            toast.error(message);
            throw err;
        } finally{
            setIsLoading(false);
        }
    }, []);
    const value = {
        isSharePointConnected,
        isLoading,
        error,
        initiateOAuthFlow,
        disconnectSharePoint,
        connectSharePoint
    };
    return /*#__PURE__*/ _jsx(SharePointAuthContext.Provider, {
        value: value,
        children: children
    });
}
export function useSharePointAuth() {
    const context = useContext(SharePointAuthContext);
    if (!context) {
        throw new Error('useSharePointAuth must be used within SharePointAuthProvider');
    }
    return context;
}
